import {
  observable,
  reaction,
} from 'mobx';
import {
  BaudRate,
  Parity,
} from '../constants/DeviceModels';
import { ProxyCommands } from '../constants/ProxyCommands';
import logger from '../utils/logger';
import { stringToHex } from '../utils/hex';

class ScoreboardStore {
  @observable
    ports = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    const { batchStore, scheduleStore } = this.rootStore;

    this.connect();

    reaction(
      () => !batchStore.isDemo,
      () => {
        this.connect();
      }
    );

    reaction(
      () => [ ...batchStore.running, ...batchStore.queue, ...scheduleStore.schedules ]
        .filter(v => v.truckCode).map(v => v.truckCode),
      () => {
        this.displayTrucks();
      }
    );
  }

  connect() {
    const { asyncStore, batchStore, pacStore, devicesStore } = this.rootStore;
    const scoreboards = devicesStore.scoreboards.filter(v => v.portCOM);

    asyncStore.clearTimeout(batchStore.sbConnectTimer);

    if (!batchStore.proxyVersion && !batchStore.isDemo) {
      if (pacStore.isWSOpen() && scoreboards.length) {
        pacStore.runWS(ProxyCommands.GET_PROXY_VERSION);
      }

      batchStore.sbConnectTimer = asyncStore.setTimeout(() => {
        this.connect();
      }, 1000);
      return;
    }

    if (!this.ports.length && !batchStore.isDemo) {
      if (pacStore.isWSOpen() && scoreboards.length) {
        pacStore.runWS(ProxyCommands.RD_GET_PORTS);
      }

      batchStore.sbConnectTimer = asyncStore.setTimeout(() => {
        this.connect();
      }, 1000);

      return;
    }

    batchStore.sbConnectTimer = asyncStore.setTimeout(() => {
      scoreboards.forEach(device => {

        if (!batchStore.isDemo) {
          if (!this.ports.find(v => v.path === `COM${device.portCOM}`)) {
            logger.log(`Port COM${device.portCOM} unavailable!`);
            return;
          }

          const rd = {
            path: device.portCOM ? `COM${device.portCOM}` : '', // 'COM3'
            baudRate: device.baudRate || BaudRate['1200'],
            dataBits: device.dataBits || 8,
            stopBits: device.stopBits || 1,
            parity: device.parity || Parity.None,
          };

          logger.log(ProxyCommands.RD_CONNECT, rd.path, rd, device);
          pacStore.runWS(`${ProxyCommands.RD_CONNECT} ${JSON.stringify(rd)}`);
        }
      });

    }, 1000);
  }

  savePorts(ports) {
    this.ports = ports;
  }

  onConnectFail(path) {
    this.ports.filter(v => v.path !== path);
  }

  getTruckData(device, queueAndDispatchTickets = []) {
    let data = [ ...queueAndDispatchTickets ]
      .map(v => v.truckCode);
    data = data
      .filter((v, i, self) => self.indexOf(v) === i)
      .slice(0, device.numberOfRows);
    data
      .forEach((d, i) => {
        d = String(d);
        const size = d.length;
        const maxSize = Number(device.numberOfDigits || 4);
        if (!d || Number.isNaN(Number(d))) {
          data[i] = '0'.repeat(maxSize);
        } else if (size > maxSize) {
          data[i] = d.slice(maxSize * -1);
        } else if (size < maxSize) {
          data[i] = ' '.repeat(maxSize - size) + d;
        } else {
          data[i] = d;
        }
      });

    return data;
  }

  getCommand(device, truckData = []) {
    return device.portCOM
      ? `COM${device.portCOM} 56 20 ${stringToHex(truckData.join(' '))} 0D`
      : '';
  }

  canSendCommand() {
    const { batchStore, pacStore } = this.rootStore;
    return batchStore.canEmit && !batchStore.replayMode && pacStore.isWSOpen();
  }

  displayTrucks() {
    const { devicesStore, pacStore, batchStore, scheduleStore } = this.rootStore;
    const { running, queue } = batchStore;
    const { schedules } = scheduleStore;
    const scoreboards = devicesStore.scoreboards.filter(v => v.portCOM);

    scoreboards.forEach(device => {
      const truckData = this.getTruckData(device, [ ...running, ...queue, ...schedules ]);

      if (this.canSendCommand()) {
        const command = this.getCommand(device, truckData);
        if (command) {
          pacStore.runWS(`${ProxyCommands.RD_WRITE} ${command}`);
          logger.log(`scoreboard rd:write ${command} => ${truckData.join('')}`);
        }
      }
    });
  }

}

export default ScoreboardStore;
